import { Injectable, inject } from '@angular/core';
import { ProductsSort } from '@clubhub/ignite/models/products/products-sort';
import { ISimpleGridFilterOperation } from '@shared/models';
import { SavedSearchCommonDataService } from '../saved-searches/saved-search-common-data.service';

@Injectable({
  providedIn: 'root',
})
export class ProductsResultSettingsService {
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);

  private defaultSort: ProductsSort = { sortOn: '', sortDir: 'asc'};
  private appliedSort: ProductsSort | undefined;
  private readonly appliedFilters: Map<string, Readonly<ISimpleGridFilterOperation>> = new Map();

  /**
   * Whether the broker's saved preferences supply the default sort, rather than the platform's.
   *
   * Read by the results component, which otherwise force-sorts to Match / True cost as sidebar filters come and
   * go. That override exists to surface the best matches when no deliberate sort was chosen; a saved preference
   * is a deliberate sort, so it is left alone.
   */
  public hasPreferredSort = false;

  public get productsSort(): ProductsSort {
    return !!this.appliedSort?.sortOn
      ? this.appliedSort
      : this.defaultSort;
  }

  public resetSort(): void {
    this.appliedSort = { sortOn: '', sortDir: 'asc'};
  }

  public setDefaultSort(sort: ProductsSort): void {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.defaultSort = sort;
  }

  /**
   * The platform's own default sort, applied as a search initialises.
   *
   * Neither of these two setters marks the search as having unsaved changes, unlike <see cref="setDefaultSort" />.
   * Neither is something the broker just did, and Evidence of Results auto-saves a search that is dirty - so
   * dirtying one at init would silently create a saved search nobody had touched.
   */
  public setPlatformDefaultSort(sort: ProductsSort): void {
    this.defaultSort = sort;
    this.hasPreferredSort = false;
  }

  /** The broker's saved sort preference, which supersedes the platform default for this search. */
  public setPreferredDefaultSort(sort: ProductsSort): void {
    this.defaultSort = sort;
    this.hasPreferredSort = true;
  }

  public setSort(sort: ProductsSort): void {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.appliedSort = sort;
  }

  public addFilter(filter: Readonly<ISimpleGridFilterOperation>) {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.appliedFilters.set(filter.column, filter);
  }

  public removeFilter(filter: Readonly<ISimpleGridFilterOperation>) {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.appliedFilters.delete(filter.column);
  }

  public clearFilters() {
    this.savedSearchCommonDataService.clearSavedSearch();
    this.appliedFilters.clear();
  }

  public getFilters() {
    return [...this.appliedFilters.values()];
  }
}
